// src/views/SynthesisConfigurations/SynthesisConfigurationForm.tsx

import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField, Paper, Button, Grid, IconButton } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { getSynthesisConfiguration, updateSynthesisConfiguration } from '../../services/synthesisConfigService';
import { SynthesisConfigurationUpdate, SynthesisConfigurationResponse } from '../../types/synthesisConfig';
import { Delete, Add } from '@mui/icons-material';
import swal from 'sweetalert';
import useAuth from '../../hooks/useAuth';

interface CouncilMemberForm {
  full_name: string;
  nickname: string;
  type: 'vereador(a)' | 'vereador(a) vice-presidente' | 'vereador(a) presidente';
}

const councilMemberTypes = [
  { value: 'vereador(a)', label: 'Vereador(a)' },
  { value: 'vereador(a) vice-presidente', label: 'Vereador(a) Vice-Presidente' },
  { value: 'vereador(a) presidente', label: 'Vereador(a) Presidente' },
];

const SynthesisConfigurationForm: React.FC = () => {
  const { cfgId } = useParams<{ cfgId: string }>();
  const isEditMode = Boolean(cfgId);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Removido o recurso de criação: se cfgId não estiver definido, redireciona
  useEffect(() => {
    if (!cfgId) {
      swal("Erro", "Criação de nova configuração não é permitida.", "error");
      navigate("/synthesis/configurations");
    }
  }, [cfgId, navigate]);

  const [entityName, setEntityName] = useState<string>('');
  const [street, setStreet] = useState<string>('');
  const [cep, setCep] = useState<string>('');
  const [councilMembers, setCouncilMembers] = useState<CouncilMemberForm[]>([
    { full_name: '', nickname: '', type: 'vereador(a)' },
  ]);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchConfig = async () => {
    if (!cfgId) return;
    setLoading(true);
    try {
      const data: SynthesisConfigurationResponse = await getSynthesisConfiguration(cfgId);
      setEntityName(data.entity_name);
      setStreet(data.address.street);
      setCep(data.address.cep);
      if (data.council_members && data.council_members.length > 0) {
        setCouncilMembers(
          data.council_members.map(member => ({
            full_name: member.full_name,
            nickname: member.nickname || '',
            type: member.type,
          }))
        );
      } else {
        setCouncilMembers([]);
      }
    } catch (error) {
      console.error("Erro ao buscar configuração:", error);
      swal("Erro", "Não foi possível buscar a configuração.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEditMode && cfgId) {
      fetchConfig();
    }
  }, [isEditMode, cfgId]);

  const handleAddMember = () => {
    setCouncilMembers([...councilMembers, { full_name: '', nickname: '', type: 'vereador(a)' }]);
  };

  const handleMemberChange = <K extends keyof CouncilMemberForm>(
    index: number,
    field: K,
    value: CouncilMemberForm[K]
  ) => {
    const updatedMembers = [...councilMembers];
    updatedMembers[index][field] = value;
    setCouncilMembers(updatedMembers);
  };

  const handleRemoveMember = (index: number) => {
    const updatedMembers = [...councilMembers];
    updatedMembers.splice(index, 1);
    setCouncilMembers(updatedMembers);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!entityName || !street || !cep) {
      swal("Erro", "Preencha todos os campos obrigatórios.", "error");
      return;
    }
    setLoading(true);
    try {
      if (isEditMode && cfgId) {
        const payload: SynthesisConfigurationUpdate = {
          // O campo "entity_name" não será alterado, mas mesmo se incluído, o backend deverá ignorá-lo.
          // Aqui, para reforçar, estamos enviando o mesmo valor que já foi carregado.
          entity_name: entityName,
          address: { street, cep },
          council_members: councilMembers
            .filter(member => member.full_name.trim() !== '')
            .map(member => ({
              full_name: member.full_name,
              nickname: member.nickname,
              type: member.type,
            })),
        };
        await updateSynthesisConfiguration(cfgId, payload);
        swal("Sucesso", "Configuração atualizada com sucesso.", "success");
      }
      navigate("/synthesis/configurations");
    } catch (error) {
      console.error("Erro ao salvar configuração:", error);
      swal("Erro", "Não foi possível salvar a configuração.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Editar Configuração do Synthesis
      </Typography>
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          {/* A entidade é determinada pelo usuário final; apenas exibida */}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Configuração para a entidade: <strong>{user?.entities && user.entities.length > 0 ? user.entities[0].name : "não definido"}</strong>
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Nome da Entidade"
                value={entityName}
                onChange={(e) => setEntityName(e.target.value)}
                fullWidth
                required
                InputProps={{ readOnly: true }}
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Rua"
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="CEP"
                value={cep}
                onChange={(e) => setCep(e.target.value)}
                fullWidth
                required
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Membros do Conselho
            </Typography>
            {councilMembers.map((member, index) => (
              <Grid container spacing={2} key={index} sx={{ mt: 1, alignItems: 'center' }}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Nome Completo"
                    value={member.full_name}
                    onChange={(e) => handleMemberChange(index, 'full_name', e.target.value)}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Apelido"
                    value={member.nickname}
                    onChange={(e) => handleMemberChange(index, 'nickname', e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={10} sm={3}>
                  <TextField
                    select
                    label="Tipo"
                    value={member.type}
                    onChange={(e) => handleMemberChange(index, 'type', e.target.value as any)}
                    fullWidth
                  >
                    {councilMemberTypes.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={2} sm={1}>
                  <IconButton 
                    onClick={() => handleRemoveMember(index)}
                    disabled={councilMembers.length === 1}
                  >
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Button variant="outlined" startIcon={<Add />} onClick={handleAddMember} sx={{ mt: 2 }}>
              Adicionar Membro
            </Button>
          </Box>
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate("/synthesis/configurations")} sx={{ mr: 2 }}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? "Salvando..." : "Salvar"}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default SynthesisConfigurationForm;